/**
 * LandingAIAdvisor — free-form investment Q&A embedded in the landing hero
 * between the DPP description and the "Create free account" CTA. Anonymous
 * users get 3 questions per browser session (server-enforced); results are
 * rendered as a horizontal bar chart + per-ticker rows with an "Add to
 * scenario" button that funnels visitors into the portfolio builder.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2, ArrowRight, TrendingUp, PlusCircle, UserPlus, Zap } from "lucide-react";
import { BarChart, Bar, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const SEED_QUESTION_FALLBACK =
  "What are the 5 highest-yield stocks or ETFs with strong price-appreciation and dividend payments, priced under $25?";

// Stable per-browser session id so the anon quota + LLM memory follow the
// same visitor across page reloads without depending on cookies.
function getAnonSessionId() {
  try {
    let sid = localStorage.getItem("dpp_anon_session_id");
    if (!sid) {
      sid = "anon-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      localStorage.setItem("dpp_anon_session_id", sid);
    }
    return sid;
  } catch { return "anon-fallback"; }
}

// Rotate through a colorblind-friendly palette so the bar chart reads at a
// glance even on the compact landing-hero footprint.
const BAR_COLORS = ["#F5A623", "#2E86AB", "#5DADE2", "#F39C12", "#48C9B0", "#AF7AC5", "#E67E22", "#5D6D7E"];

function fmtPct(v) {
  if (v == null) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}%`;
}
function fmt$(v) {
  if (v == null) return "—";
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function LandingAIAdvisor() {
  const { user } = useAuth();
  const [featured, setFeatured] = useState([]);   // rotating seed questions from backend
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState(false);
  const [rotIdx, setRotIdx] = useState(0);
  const nav = useNavigate();
  const responseRef = useRef(null);
  const lastQuestionRef = useRef("");  // remember what was asked for tracking

  // Fetch the current featured questions on mount — cycles based on 7-day
  // conversion data (add_all/asked ratio) so the winners auto-promote.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/landing/advisor/featured");
        if (!cancelled && r.data?.questions?.length) {
          setFeatured(r.data.questions);
        }
      } catch (_) { /* fall back to the SEED_QUESTION_FALLBACK placeholder */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Rotating placeholder — cycles through featured prompts every 3.5s so the
  // input feels alive at first glance. Pauses whenever the input is focused
  // or the visitor has started typing so we never overwrite their intent.
  useEffect(() => {
    if (focused || question.length > 0 || featured.length === 0) return;
    const id = setInterval(() => {
      setRotIdx((i) => (i + 1) % featured.length);
    }, 3500);
    return () => clearInterval(id);
  }, [focused, question, featured.length]);

  const currentPlaceholder = featured.length > 0
    ? (featured[rotIdx]?.question || SEED_QUESTION_FALLBACK)
    : SEED_QUESTION_FALLBACK;

  const track = (event, extra = {}) => {
    // Fire-and-forget — never blocks UX. Backend logs everything for the
    // /api/landing/advisor/funnel + /featured aggregations.
    try {
      api.post(
        "/landing/advisor/track",
        { event, ...extra },
        { headers: { "X-Anon-Session": getAnonSessionId() } },
      ).catch(() => {});
    } catch (_) {}
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    // If the visitor hits "Ask" without typing, treat the rotating placeholder
    // as their intent — the input already reads like an example prompt, so
    // this makes the CTA feel magical instead of scolding.
    const q = (question.trim() || currentPlaceholder.trim());
    if (q.length < 3) { setError("Please type a question first"); return; }
    setError("");
    setLoading(true);
    setResult(null);
    lastQuestionRef.current = q;
    try {
      const r = await api.post(
        "/landing/ask-advisor",
        { question: q },
        { headers: { "X-Anon-Session": getAnonSessionId() } },
      );
      setResult(r.data);
      requestAnimationFrame(() => {
        try { responseRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch (_) {}
      });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Something went wrong — try again";
      if (status === 429) {
        toast.error(detail, {
          action: { label: "Create free account", onClick: () => nav("/auth?mode=register") },
          duration: 12000,
        });
      }
      setError(detail);
    } finally { setLoading(false); }
  };

  const chartData = useMemo(() => {
    if (!result?.tickers) return [];
    return result.tickers.map((t, i) => ({
      ticker: t.ticker,
      total: t.total_return_pct ?? 0,
      color: BAR_COLORS[i % BAR_COLORS.length],
    }));
  }, [result]);

  const addAllToScenario = () => {
    if (!result?.tickers?.length) return;
    const tickers = result.tickers.map((t) => t.ticker);
    track("add_all", { question: lastQuestionRef.current, tickers });
    nav(`/portfolio/new?seed=${encodeURIComponent(tickers.join(","))}`);
  };
  const addOneToScenario = (ticker) => {
    track("add_one", { question: lastQuestionRef.current, ticker });
    nav(`/portfolio/new?seed=${encodeURIComponent(ticker)}`);
  };

  // Signup-with-seed: for anon users, offer a shortcut that stashes the AI's
  // ticker list to localStorage and jumps to /auth?mode=register&intent=advisor-seed.
  // Auth.jsx post-register handler auto-creates a portfolio from the stash —
  // turns disposable research into a save-worthy artifact from the first tap.
  const signupWithSeed = () => {
    if (!result?.tickers?.length) return;
    const tickers = result.tickers.map((t) => t.ticker);
    try {
      localStorage.setItem("dp_advisor_seed", JSON.stringify({
        tickers,
        question: lastQuestionRef.current,
        savedAt: new Date().toISOString(),
      }));
    } catch (_) {}
    track("add_all", { question: lastQuestionRef.current, tickers, intent: "signup" });
    nav(`/auth?mode=register&intent=advisor-seed`);
  };

  return (
    <div data-testid="landing-ai-advisor">
      <Card className="border border-primary/15 bg-primary/[0.04] shadow-sm rounded-2xl">
        <CardContent className="p-6 lg:p-8">
          {!result && (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:gap-8 items-start">
              {/* LEFT — headline + input row */}
              <div>
                <div className="mono text-[9px] uppercase tracking-[0.25em] text-primary flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Personal AI Agent
                </div>
                <h2 className="serif text-2xl sm:text-3xl mt-1">Ask DPP — Your Personal AI Agent</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Find dividend stocks under $25 with growth potential — or ask about any ticker, strategy, or your own portfolio once you're signed in.
                </p>
                <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2" data-testid="advisor-form">
                  <Input
                    data-testid="advisor-question-input"
                    value={question}
                    onChange={(e) => { setError(""); setQuestion(e.target.value); }}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    placeholder={currentPlaceholder}
                    className="flex-1 h-11 rounded-lg bg-card"
                    disabled={loading}
                    maxLength={400}
                  />
                  <Button
                    type="submit"
                    data-testid="advisor-submit"
                    disabled={loading}
                    aria-busy={loading}
                    className="h-11 rounded-lg px-6 font-semibold active:scale-[0.98] transition-transform bg-primary hover:brightness-110"
                  >
                    {loading ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Thinking…</>) : (<>Find Investments</>)}
                  </Button>
                </form>
                {error && !loading && (
                  <div className="mt-2 text-xs text-destructive" role="alert">{error}</div>
                )}
              </div>

              {/* RIGHT — "Or try an example" pill grid.
                  We show 4 pills in a 2-col grid so it visually reads as a
                  compact reference card next to the primary input. */}
              {featured.length > 0 && (
                <div data-testid="advisor-featured-pills">
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                    Or try an example
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {featured.slice(0, 4).map((f, i) => {
                      // Trim to keep pills equal height without overflow.
                      const label = f.question.length > 32 ? f.question.slice(0, 30) + "…" : f.question;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setQuestion(f.question)}
                          disabled={loading}
                          title={f.question}
                          className="text-xs px-3 py-2 rounded-lg border border-primary/25 bg-card hover:bg-primary/10 transition-colors text-foreground/80 hover:text-primary text-left truncate"
                          data-testid={`advisor-example-${i}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Response */}
          {result && (
            <div ref={responseRef} data-testid="advisor-response">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-secondary/15 grid place-items-center text-secondary">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <div className="mono text-[10px] uppercase tracking-widest text-secondary">DPP AI · Response</div>
                  <div className="serif text-lg leading-tight">Here's what I found for you</div>
                </div>
              </div>
              <p className="text-sm text-foreground/90 leading-relaxed mb-3">{result.answer_narrative}</p>
              {chartData.length > 0 && (
                <div className="mb-4 rounded-lg bg-background/40 p-2 border border-border">
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Estimated 12-mo total return (upside + yield)
                  </div>
                  <ResponsiveContainer width="100%" height={140}>
                      <BarChart data={chartData} margin={{ top: 4, right: 10, left: -20, bottom: 0 }}>
                      <XAxis dataKey="ticker" stroke="currentColor" fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke="currentColor" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
                      <Tooltip
                        cursor={{ fill: "rgba(0,0,0,0.05)" }}
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                        formatter={(v) => [`${v.toFixed(2)}%`, "Total return"]}
                      />
                      <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                        {chartData.map((d, i) => (<Cell key={i} fill={d.color} />))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Per-ticker table */}
              <div className="rounded-lg border border-border overflow-hidden bg-background/40">
                <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,0.9fr)_minmax(0,auto)] gap-2 px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <div>Ticker</div><div className="text-right">Price</div><div className="text-right">Div yield</div><div className="text-right">Analyst target</div><div className="text-right">Upside</div><div className="text-right">Total return</div><div />
                </div>
                {result.tickers.map((t, i) => (
                  <div
                    key={t.ticker}
                    className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,0.9fr)_minmax(0,auto)] gap-2 items-center px-3 py-2 text-sm border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors"
                    data-testid={`advisor-row-${t.ticker}`}
                  >
                    <div className="min-w-0">
                      <div className="mono font-semibold" style={{ color: BAR_COLORS[i % BAR_COLORS.length] }}>{t.ticker}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{t.name}</div>
                    </div>
                    <div className="mono text-right">{fmt$(t.price)}</div>
                    <div className="mono text-right">{fmtPct(t.dividend_yield_pct)}</div>
                    <div className="mono text-right">{fmt$(t.analyst_target)}</div>
                    <div className={`mono text-right ${t.upside_pct == null ? "" : (t.upside_pct >= 0 ? "text-primary" : "text-destructive")}`}>{fmtPct(t.upside_pct)}</div>
                    <div className={`mono text-right font-semibold ${t.total_return_pct == null ? "" : (t.total_return_pct >= 0 ? "text-primary" : "text-destructive")}`}>{fmtPct(t.total_return_pct)}</div>
                    <button
                      type="button"
                      onClick={() => addOneToScenario(t.ticker)}
                      data-testid={`advisor-add-${t.ticker}`}
                      className="mono text-[10px] uppercase tracking-widest text-primary hover:text-primary/80 transition-colors flex items-center gap-1 whitespace-nowrap"
                      title={`Start a new portfolio scenario with ${t.ticker}`}
                    >
                      <PlusCircle className="w-3.5 h-3.5" /> Add
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  {result.cache_hit && <Zap className="w-3 h-3 text-secondary" title="Instant answer from cache" />}
                  {typeof result.quota_used === "number"
                    ? `${result.quota_used} of ${result.quota_limit} free questions used`
                    : "Unmetered · thanks for being a Pro member"}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  {!user && (
                    <Button
                      onClick={signupWithSeed}
                      data-testid="advisor-signup-with-seed"
                      variant="outline"
                      className="rounded-full font-semibold active:scale-[0.98] transition-transform border-primary/40 hover:bg-primary/5"
                      title="Sign up and we'll save this list as your first portfolio"
                    >
                      <UserPlus className="w-4 h-4 mr-1.5" />
                      Save as my portfolio
                    </Button>
                  )}
                  <Button
                    onClick={addAllToScenario}
                    data-testid="advisor-add-all"
                    className="rounded-full font-semibold active:scale-[0.98] transition-transform"
                  >
                    Add all {result.tickers.length} to a scenario <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
